import { API_URL, ENDPOINTS } from './endpoints';

// ============================================================
// INTERCEPTOR GLOBAL DE AUTENTICACIÓN
// ============================================================

const handleUnauthorized = () => {
    localStorage.removeItem('artemis_token');
    localStorage.removeItem('artemis_user');
    window.location.href = '/login';
};

/**
 * Wrapper de fetch que redirige al login automáticamente si el servidor
 * devuelve 401 (token expirado o inválido).
 */
const apiFetch = async (url, options = {}) => {
    const response = await fetch(url, options);
    if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Sesión expirada. Por favor inicia sesión nuevamente.');
    }
    return response;
};

// ============================================================
// AUTENTICACIÓN
// ============================================================

/**
 * Autentica un usuario (cualquier rol con UserType != 'none') con email y contraseña.
 * Retorna una de dos formas:
 *   - { access_token, token_type, user }               → sesión lista
 *   - { requires_selection: true, selection_token, tenants: [...] }
 *       → el correo existe en varios establecimientos; el usuario debe elegir
 *         uno y llamar a selectTenant().
 * Lanza un Error si las credenciales son inválidas o hay un error de red.
 */
export const loginDocente = async (email, password) => {
    const response = await fetch(ENDPOINTS.AUTH_LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Credenciales inválidas');
    }

    return response.json();
};

/**
 * Segundo paso del login: elige la combinación establecimiento+rol a la que
 * conectarse (si la persona tiene más de un rol en el mismo establecimiento,
 * cada uno tiene su propio organization_person_role_id).
 * Retorna { access_token, token_type, user }.
 */
export const selectTenant = async (selectionToken, organizationPersonRoleId) => {
    const response = await fetch(ENDPOINTS.AUTH_LOGIN_SELECT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            selection_token: selectionToken,
            organization_person_role_id: organizationPersonRoleId,
        }),
    });

    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'No se pudo completar el inicio de sesión');
    }

    return response.json();
};

/**
 * El usuario autenticado fija su contraseña definitiva (primer ingreso tras un
 * reset del administrador). Retorna { access_token, token_type, user } con
 * must_change_password ya en false.
 */
export const changePassword = async (newPassword) => {
    const response = await fetch(ENDPOINTS.AUTH_CHANGE_PASSWORD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ new_password: newPassword }),
    });

    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'No se pudo cambiar la contraseña');
    }

    return response.json();
};

/**
 * "Cambiar Vista": el usuario ya autenticado elige otro de sus roles activos
 * en el MISMO establecimiento (botón en la barra de navegación). No pide
 * contraseña. Retorna { access_token, token_type, user } con el nuevo rol.
 */
export const switchRole = async (organizationPersonRoleId) => {
    const response = await fetch(ENDPOINTS.AUTH_SWITCH_ROLE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ organization_person_role_id: organizationPersonRoleId }),
    });

    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'No se pudo cambiar de vista');
    }

    return response.json();
};

/** Retorna headers con el JWT almacenado en localStorage (si existe). */
export const authHeaders = () => {
    const token = localStorage.getItem('artemis_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getCourses = async () => {
    try {
        console.log('Haciendo petición a:', `${API_URL}/courses`);
        const response = await apiFetch(ENDPOINTS.COURSES, {
            headers: authHeaders()
        });
        console.log('Respuesta recibida:', response.status, response.statusText);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('Datos parseados:', data);
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching courses:', error);
        return [];
    }
};

export const getSubjectsByCourse = async (courseId) => {
    try {
        const response = await apiFetch(ENDPOINTS.COURSE_SUBJECTS(courseId), {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching subjects:', error);
        return [];
    }
};

export const getUnitsBySubject = async (subjectId) => {
    try {
        const response = await apiFetch(ENDPOINTS.SUBJECT_UNITS(subjectId), {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching units:', error);
        return [];
    }
};

export const getUnitsByCourse = async (courseId, sectionId = null) => {
    try {
        console.log('Haciendo petición a:', ENDPOINTS.COURSE_SECTIONS_BY_COURSE(courseId, sectionId));
        const response = await apiFetch(ENDPOINTS.COURSE_SECTIONS_BY_COURSE(courseId, sectionId), {
            headers: authHeaders()
        });
        console.log('Respuesta recibida:', response.status, response.statusText);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('Datos de unidades parseados:', data);
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching units by course:', error);
        return [];
    }
};


// Persiste las fechas de inicio/fin de una unidad para UNA sección de curso
// puntual (override en coursesectionoadate del tenant; no afecta a otras
// secciones/tenants que compartan la misma Unidad del catálogo global).
// startDate/endDate son 'YYYY-MM-DD' o '' (se envía null).
export const updateUnitValidDates = async (unitId, courseSectionId, startDate, endDate) => {
    const response = await apiFetch(ENDPOINTS.UNIT_VALID_DATES(unitId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
            course_section_id: courseSectionId,
            valid_start_date: startDate || null,
            valid_end_date: endDate || null,
        }),
    });
    if (!response.ok) {
        let detail = 'Error al guardar las fechas de la unidad';
        try { detail = (await response.json()).detail || detail; } catch (e) { /* noop */ }
        throw new Error(detail);
    }
    return response.json();
};

export const getOasByUnit = async (unitId, sectionId = null) => {
    try {
        const response = await apiFetch(ENDPOINTS.OAS_BY_UNIT(unitId, sectionId), {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching OAs:', error);
        return [];
    }
};

export const savePlanning = async (planningData) => {
    try {
        const response = await apiFetch(ENDPOINTS.PLANNING_SAVE, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...authHeaders()
            },
            body: JSON.stringify(planningData)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error saving planning:', error);
        throw error;
    }
};

export const getPlannedClassesByOa = async (oaId) => {
    const response = await apiFetch(ENDPOINTS.OA_PLANNED_CLASSES(oaId), {
        headers: authHeaders()
    });
    return await response.json();
};

// Nuevos endpoints para funcionalidades adicionales
/**
 * Crea una nueva clase planificada o actualiza una existente con soporte para archivos adjuntos.
 * Usa FormData para enviar datos y archivos.
 *
 * Si se proporciona planned_class_id y la clase existe, se actualiza.
 * Si no se proporciona planned_class_id o la clase no existe, se crea una nueva.
 *
 * @param {Object} plannedClassData - Datos de la clase planificada
 * @param {number} plannedClassData.competency_definition_id
 * @param {number} plannedClassData.course_section_id
 * @param {string} plannedClassData.class_date
 * @param {string} plannedClassData.type
 * @param {string} [plannedClassData.objective]
 * @param {Object} [plannedClassData.stages]
 * @param {Array} [plannedClassData.comments]
 * @param {number} [plannedClassData.planned_class_id] - ID opcional para actualizar en lugar de crear
 * @param {Array<string>} [plannedClassData.keep_attachments] - Paths de attachments existentes a mantener (solo para actualización)
 * @param {Array<File>} [files] - Archivos a adjuntar
 */
export const createPlannedClass = async (plannedClassData, files = []) => {
    const formData = new FormData();

    // Agregar campos obligatorios
    formData.append('competency_definition_id', plannedClassData.competency_definition_id);
    formData.append('course_section_id', plannedClassData.course_section_id);
    formData.append('class_date', plannedClassData.class_date);
    formData.append('type', plannedClassData.type);

    // Agregar campos opcionales
    if (plannedClassData.objective) {
        formData.append('objective', plannedClassData.objective);
    }

    if (plannedClassData.stages) {
        formData.append('stages', JSON.stringify(plannedClassData.stages));
    }

    if (plannedClassData.plan_template) {
        formData.append('plan_template', plannedClassData.plan_template);
    }

    if (plannedClassData.template_fields) {
        formData.append('template_fields', JSON.stringify(plannedClassData.template_fields));
    }

    if (plannedClassData.comments && plannedClassData.comments.length > 0) {
        formData.append('comments', JSON.stringify(plannedClassData.comments));
    }

    // Agregar planned_class_id si se proporciona (para actualización)
    if (plannedClassData.planned_class_id !== undefined && plannedClassData.planned_class_id !== null) {
        formData.append('planned_class_id', plannedClassData.planned_class_id);
    }

    // Agregar keep_attachments si se proporciona (solo para actualización)
    if (plannedClassData.keep_attachments && plannedClassData.keep_attachments.length > 0) {
        formData.append('keep_attachments', JSON.stringify(plannedClassData.keep_attachments));
    }

    // Agregar archivos
    if (files && files.length > 0) {
        files.forEach(file => {
            formData.append('files', file);
        });
    }

    const response = await apiFetch(ENDPOINTS.PLANNED_CLASSES, {
        method: 'POST',
        // NO establecer Content-Type - el navegador lo hace automáticamente con el boundary correcto
        headers: authHeaders(),
        body: formData,
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al crear/actualizar la clase planificada');
    }

    return await response.json();
};

/**
 * Actualiza una clase planificada con soporte para archivos adjuntos.
 *
 * @param {number} plannedClassId - ID de la clase a actualizar
 * @param {Object} plannedClassData - Datos a actualizar
 * @param {Array<File>} [newFiles] - Nuevos archivos a agregar
 * @param {Array<string>} [keepAttachmentPaths] - Paths de attachments existentes a mantener
 */
export const updatePlannedClass = async (plannedClassId, plannedClassData, newFiles = [], keepAttachmentPaths = []) => {
    const formData = new FormData();

    // Agregar campos opcionales
    if (plannedClassData.type !== undefined) {
        formData.append('type', plannedClassData.type);
    }

    if (plannedClassData.objective !== undefined) {
        formData.append('objective', plannedClassData.objective);
    }

    if (plannedClassData.stages !== undefined) {
        formData.append('stages', JSON.stringify(plannedClassData.stages));
    }

    if (plannedClassData.plan_template !== undefined) {
        formData.append('plan_template', plannedClassData.plan_template);
    }

    if (plannedClassData.template_fields !== undefined) {
        formData.append('template_fields', JSON.stringify(plannedClassData.template_fields));
    }

    if (plannedClassData.comments !== undefined) {
        formData.append('comments', JSON.stringify(plannedClassData.comments));
    }

    // Paths de archivos a mantener
    if (keepAttachmentPaths && keepAttachmentPaths.length > 0) {
        formData.append('keep_attachments', JSON.stringify(keepAttachmentPaths));
    }

    // Nuevos archivos
    if (newFiles && newFiles.length > 0) {
        newFiles.forEach(file => {
            formData.append('files', file);
        });
    }

    const response = await apiFetch(ENDPOINTS.PLANNED_CLASS(plannedClassId), {
        method: 'PUT',
        headers: authHeaders(),
        body: formData,
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al actualizar la clase planificada');
    }

    return await response.json();
};

export const deletePlannedClass = async (plannedClassId) => {
    const response = await apiFetch(ENDPOINTS.PLANNED_CLASS(plannedClassId), {
        method: 'DELETE',
        headers: authHeaders(),
    });
    return await response.json();
};

export const deleteUnit0 = async (courseId) => {
    try {
        console.log('Eliminando Unidad 0 para curso:', courseId);
        const response = await apiFetch(ENDPOINTS.ADMIN_DELETE_UNIT0(courseId), {
            method: 'DELETE',
            headers: authHeaders()
        });
        console.log('Respuesta de eliminación Unidad 0:', response.status, response.statusText);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('Resultado eliminación Unidad 0:', data);
        return data;
    } catch (error) {
        console.error('Error deleting Unit 0:', error);
        throw error;
    }
};

// Funciones para el dashboard
export const getDashboardSummary = async () => {
    try {
        const response = await apiFetch(ENDPOINTS.DASHBOARD_SUMMARY, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching dashboard summary:', error);
        throw error;
    }
};

export const getPlanningStatsBySection = async () => {
    try {
        const response = await apiFetch(ENDPOINTS.DASHBOARD_PLANNING_STATS_BY_SECTION, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching planning stats by section:', error);
        throw error;
    }
};

export const getTopUnplannedSections = async (limit = 10) => {
    try {
        const response = await apiFetch(`${ENDPOINTS.DASHBOARD_TOP_UNPLANNED_SECTIONS}?limit=${limit}`, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching top unplanned sections:', error);
        throw error;
    }
};

export const getCoursePlanningProgress = async () => {
    try {
        const response = await apiFetch(ENDPOINTS.DASHBOARD_COURSE_PLANNING_PROGRESS, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching course planning progress:', error);
        throw error;
    }
};

// ============================================================
// API FUNCTIONS FOR PLANNING REPLICATION
// ============================================================

export const getReplicatePlanningStats = async (sourceSectionId, destSectionId, baseDate) => {
    const response = await apiFetch(
        `${ENDPOINTS.PLANNED_CLASS_REPLICATE_PLANNING_STATS}?source_section_id=${sourceSectionId}&dest_section_id=${destSectionId}&base_date=${baseDate}`,
        { headers: authHeaders() }
    );
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Error al obtener estadísticas de replicación');
    }
    return response.json();
};

export const getReplicatePlanningPreview = async (sourceSectionId, destSectionId, baseDate) => {
    const response = await apiFetch(
        `${ENDPOINTS.PLANNED_CLASS_REPLICATE_PLANNING}?source_section_id=${sourceSectionId}&dest_section_id=${destSectionId}&base_date=${baseDate}`,
        { headers: authHeaders() }
    );
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Error al obtener previsualización de replicación');
    }
    return response.json();
};

export const clearSectionPlanning = async (sectionId) => {
    const response = await apiFetch(
        ENDPOINTS.PLANNED_CLASS_CLEAR_PLANNING(sectionId),
        { method: 'POST', headers: authHeaders() }
    );
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Error al limpiar la planificación');
    }
    return response.json();
};

// ============================================================
// API FUNCTIONS FOR CLASS EXECUTION TRACKING
// ============================================================

export const getPlannedClassesWithExecutionStatus = async (sectionId, authToken = null) => {
    try {
        // Agregar timestamp para evitar caché del navegador y asegurar datos frescos
        const timestamp = new Date().getTime();
        // Use provided token first; fall back to localStorage via authHeaders()
        const token = authToken || localStorage.getItem('artemis_token');
        const response = await apiFetch(`${ENDPOINTS.CLASS_EXECUTION_PLANNED_CLASSES(sectionId)}?t=${timestamp}`, {
            cache: 'no-cache',
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {})
            }
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log(`[API] Datos de clases planificadas obtenidos: ${Array.isArray(data) ? data.length : 0} clases`);
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching planned classes with execution status:', error);
        return [];
    }
};

export const getClassExecutionReasons = async () => {
    try {
        const response = await apiFetch(ENDPOINTS.CLASS_EXECUTION_REASONS, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching class execution reasons:', error);
        return [];
    }
};

export const getEvaluationReasons = async () => {
    try {
        const response = await apiFetch(ENDPOINTS.EVALUATION_REASONS, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching evaluation reasons:', error);
        return [];
    }
};

export const batchSaveClassExecution = async (batchData) => {
    try {
        const response = await apiFetch(ENDPOINTS.CLASS_EXECUTION_BATCH_SAVE, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...authHeaders()
            },
            body: JSON.stringify(batchData)
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Error al guardar ejecuciones en lote');
        }

        return await response.json();
    } catch (error) {
        console.error('Error batch saving class execution:', error);
        throw error;
    }
};

// ============================================================
// API FUNCTIONS FOR UTP VIEW
// ============================================================

export const getUTPAllPlannedClasses = async (courseId = null, subjectId = null) => {
    try {
        let url = ENDPOINTS.DASHBOARD_UTP_ALL_PLANNED_CLASSES;
        const params = new URLSearchParams();

        if (courseId) params.append('course_id', courseId);
        if (subjectId) params.append('subject_id', subjectId);

        if (params.toString()) {
            url += `?${params.toString()}`;
        }

        const response = await apiFetch(url, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching UTP planned classes:', error);
        return [];
    }
};

export const getUTPPlanningSummary = async () => {
    try {
        const response = await apiFetch(ENDPOINTS.DASHBOARD_UTP_PLANNING_SUMMARY, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching UTP planning summary:', error);
        return {
            total_oas: 0,
            planned_oas: 0,
            percentage: 0,
            total_planned_classes: 0,
            classes_by_course: []
        };
    }
};

export const getCoursesWithSubjects = async () => {
    try {
        const response = await apiFetch(ENDPOINTS.DASHBOARD_UTP_COURSES_WITH_SUBJECTS, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching courses with subjects:', error);
        return [];
    }
};

export const getUTPTeacherStatus = async () => {
    try {
        const response = await apiFetch(ENDPOINTS.DASHBOARD_UTP_TEACHER_STATUS, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching UTP teacher status:', error);
        return [];
    }
};

export const downloadUTPPlanningReport = async () => {
    const response = await apiFetch(ENDPOINTS.DASHBOARD_UTP_PLANNING_REPORT_EXPORT, {
        headers: authHeaders()
    });
    if (!response.ok) {
        throw new Error('Error al generar el reporte');
    }
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename=([^;]+)/);
    const filename = match ? match[1] : 'reporte_planificacion.xlsx';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

export const getUTPWeeklyStats = async () => {
    try {
        const response = await apiFetch(ENDPOINTS.DASHBOARD_UTP_WEEKLY_STATS, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching UTP weekly stats:', error);
        return { planned_this_week: 0, executed_this_week: 0 };
    }
};

export const getUTPStatusThresholds = async () => {
    try {
        const response = await apiFetch(ENDPOINTS.DASHBOARD_UTP_STATUS_THRESHOLDS, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching UTP status thresholds:', error);
        return { al_dia_threshold: 70, atrasado_threshold: 50 };
    }
};

export const getUTPWeeklyTrend = async (weeks = 8) => {
    try {
        const response = await apiFetch(`${ENDPOINTS.DASHBOARD_UTP_WEEKLY_TREND}?weeks=${weeks}`, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching UTP weekly trend:', error);
        return [];
    }
};

export const getUTPSemesterExecutionStats = async () => {
    try {
        const response = await apiFetch(ENDPOINTS.DASHBOARD_UTP_SEMESTER_EXECUTION_STATS, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching UTP semester execution stats:', error);
        return { planned_semester: 0, executed_semester: 0 };
    }
};

export const getPlannedClassFullDetail = async (plannedClassId) => {
    try {
        const response = await apiFetch(ENDPOINTS.PLANNED_CLASS_FULL_DETAIL(plannedClassId), {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching planned class full detail:', error);
        return null;
    }
};

export const getUTPTeacherMonitoring = async (startDate, endDate) => {
    try {
        const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
        const response = await apiFetch(`${ENDPOINTS.DASHBOARD_UTP_TEACHER_MONITORING}?${params}`, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching UTP teacher monitoring:', error);
        return [];
    }
};

export const getUTPTeacherSectionMonitoring = async (teacherName, startDate, endDate) => {
    try {
        const params = new URLSearchParams({ teacher_name: teacherName, start_date: startDate, end_date: endDate });
        const response = await apiFetch(`${ENDPOINTS.DASHBOARD_UTP_TEACHER_SECTION_MONITORING}?${params}`, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching UTP teacher section monitoring:', error);
        return [];
    }
};

// ============================================================
// API FUNCTIONS FOR NOTIFICATIONS
// ============================================================

export const getNotifications = async () => {
    const response = await apiFetch(ENDPOINTS.NOTIFICATIONS, {
        headers: authHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener notificaciones');
    return response.json();
};

export const markNotificationAsRead = async (notificationId) => {
    const response = await apiFetch(ENDPOINTS.NOTIFICATION_READ(notificationId), {
        method: 'PUT',
        headers: authHeaders()
    });
    if (!response.ok) throw new Error('Error al marcar notificación como leída');
    return response.json();
};

export const markAllNotificationsAsRead = async () => {
    const response = await apiFetch(ENDPOINTS.NOTIFICATIONS_READ_ALL, {
        method: 'PUT',
        headers: authHeaders()
    });
    if (!response.ok) throw new Error('Error al marcar todas las notificaciones como leídas');
    return response.json();
};

export const addCommentToPlannedClass = async (plannedClassId, title, content) => {
    const response = await apiFetch(ENDPOINTS.PLANNED_CLASS_COMMENTS(plannedClassId), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders()
        },
        body: JSON.stringify({ title, content })
    });
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Error al agregar comentario');
    }
    return response.json();
};

export const updatePlannedClassApproval = async (plannedClassId, approvalStatus) => {
    const response = await apiFetch(ENDPOINTS.PLANNED_CLASS_APPROVAL(plannedClassId), {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders()
        },
        body: JSON.stringify({ approval_status: approvalStatus })
    });
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Error al actualizar estado de aprobación');
    }
    return response.json();
};

export const requestPlannedClassChange = async (plannedClassId) => {
    const response = await apiFetch(ENDPOINTS.PLANNED_CLASS_CHANGE_REQUEST(plannedClassId), {
        method: 'POST',
        headers: authHeaders()
    });
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Error al enviar solicitud de cambio');
    }
    return response.json();
};

// ============================================================
// API FUNCTIONS FOR CLASS REPLICATION
// ============================================================

/**
 * Obtiene las secciones del mismo curso que tienen clases planificadas
 * con el mismo objetivo de aprendizaje.
 *
 * @param {number} courseSectionId - ID de la sección actual
 * @param {number} competencyDefinitionId - ID del objetivo de aprendizaje
 * @returns {Promise<Array>} Lista de secciones con clases planificadas
 */
export const getSectionsWithPlannedClasses = async (courseSectionId, competencyDefinitionId) => {
    try {
        const response = await apiFetch(
            ENDPOINTS.PLANNED_CLASS_SECTIONS_WITH_CLASSES(courseSectionId, competencyDefinitionId),
            { headers: authHeaders() }
        );
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching sections with planned classes:', error);
        return [];
    }
};

/**
 * Obtiene las clases planificadas de una sección específica
 * que tienen un objetivo de aprendizaje específico.
 *
 * @param {number} sectionId - ID de la sección
 * @param {number} competencyDefinitionId - ID del objetivo de aprendizaje
 * @returns {Promise<Array>} Lista de clases planificadas
 */
export const getPlannedClassesBySectionAndOA = async (sectionId, competencyDefinitionId) => {
    try {
        const response = await apiFetch(
            ENDPOINTS.PLANNED_CLASS_BY_SECTION_AND_OA(sectionId, competencyDefinitionId),
            { headers: authHeaders() }
        );
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching planned classes by section and OA:', error);
        return [];
    }
};

/**
 * Replica una clase planificada de una sección origen a una clase destino.
 * Copia: tipo de clase, objetivo, etapas y archivos adjuntos.
 * Agrega un comentario indicando que la clase fue replicada.
 *
 * @param {number} targetPlannedClassId - ID de la clase destino
 * @param {object} replicateData - Datos de replicación
 * @param {number} replicateData.source_course_section_id - ID de la sección origen
 * @param {number} replicateData.source_planned_class_id - ID de la clase origen
 * @param {string} [replicateData.replication_datetime] - Fecha y hora de replicación (opcional)
 * @returns {Promise<object>} Resultado de la replicación
 */
export const replicatePlannedClass = async (targetPlannedClassId, replicateData) => {
    try {
        const response = await apiFetch(
            ENDPOINTS.PLANNED_CLASS_REPLICATE(targetPlannedClassId),
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders()
                },
                body: JSON.stringify(replicateData)
            }
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Error al replicar la clase');
        }

        return await response.json();
    } catch (error) {
        console.error('Error replicating planned class:', error);
        throw error;
    }
};

// ============================================================
// IA — Generación de planificaciones
// ============================================================

export const generateAIPlanning = async (payload, bulkSessionId = null) => {
    const token = localStorage.getItem('artemis_token');
    const url = bulkSessionId
        ? `${ENDPOINTS.AI_GENERATE_PLANNING}?bulk_session_id=${bulkSessionId}`
        : ENDPOINTS.AI_GENERATE_PLANNING;
    const response = await apiFetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al generar planificación');
    }
    return response.json();
};

// ============================================================
// Plantilla de Planificación de Clases (básico | secuencia_didactica | dua)
// ============================================================

/**
 * Plantilla de Planificación de Clases vigente para el establecimiento del
 * usuario actual (Vista Docente) — determina qué componente de modal usar.
 * Siempre se resuelve en el servidor; cae a 'basico' si la petición falla.
 */
export const getActivePlanningTemplate = async () => {
    try {
        const response = await apiFetch(ENDPOINTS.AI_ACTIVE_PLANNING_TEMPLATE, {
            headers: authHeaders()
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return await response.json(); // { template: 'basico' | 'secuencia_didactica' | 'dua' }
    } catch (error) {
        console.error('Error fetching active planning template:', error);
        return { template: 'basico' };
    }
};

/** Configuración de plantilla del establecimiento (Vista Admin). */
export const getPlanningTemplateSetting = async () => {
    const response = await apiFetch(ENDPOINTS.ADMIN_PLANNING_TEMPLATE, {
        headers: authHeaders()
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al obtener la plantilla de planificación');
    }
    return response.json(); // { template, available_templates, entitled_advanced }
};

/** Cambia la plantilla de Planificación de Clases del establecimiento. */
export const updatePlanningTemplateSetting = async (template) => {
    const response = await apiFetch(ENDPOINTS.ADMIN_PLANNING_TEMPLATE, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ template })
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al actualizar la plantilla de planificación');
    }
    return response.json();
};

// Sesión de generación masiva: agrupa todas las clases de un lote para que la
// tabla de auditoría aigenerationlog quede vinculada (bulksessionid).
export const createAIBulkSession = async (courseSectionId, totalRequested) => {
    const response = await apiFetch(ENDPOINTS.AI_BULK_SESSION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ course_section_id: courseSectionId, total_requested: totalRequested })
    });
    if (!response.ok) throw new Error('Error al crear la sesión de generación masiva');
    const data = await response.json();
    return data.bulk_session_id;
};

export const updateAIBulkSession = async (bulkSessionId, { totalSucceeded, totalFailed, status }) => {
    const response = await apiFetch(ENDPOINTS.AI_BULK_SESSION_UPDATE(bulkSessionId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ total_succeeded: totalSucceeded, total_failed: totalFailed, status })
    });
    if (!response.ok) throw new Error('Error al actualizar la sesión de generación masiva');
    return response.json();
};

// Vincula una generación de IA con la clase planificada que el docente aceptó
// (aigenerationlog.plannedclassid / wasaccepted). Best-effort: no debe romper el
// guardado de la planificación si falla.
export const linkAIGenerationToPlannedClass = async (generationLogId, plannedClassId, wasAccepted = true) => {
    try {
        await apiFetch(ENDPOINTS.AI_LINK_PLANNED_CLASS, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({
                generation_log_id: generationLogId,
                planned_class_id: plannedClassId,
                was_accepted: wasAccepted
            })
        });
    } catch (e) {
        console.error('[AI] No se pudo vincular la generación con la clase planificada:', e);
    }
};

// ============================================================
// Dotación Docente
// ============================================================

export const getTeacherStaffingOverview = async () => {
    try {
        const response = await apiFetch(ENDPOINTS.TEACHER_STAFFING_OVERVIEW, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching teacher staffing overview:', error);
        return [];
    }
};

export const getSectionPlannedClassDates = async (sectionId, fromDate) => {
    try {
        const url = fromDate
            ? `${ENDPOINTS.TEACHER_STAFFING_SECTION_PLANNED_CLASSES(sectionId)}?from_date=${fromDate}`
            : ENDPOINTS.TEACHER_STAFFING_SECTION_PLANNED_CLASSES(sectionId);
        const response = await apiFetch(url, { headers: authHeaders() });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching section planned classes:', error);
        return [];
    }
};

export const searchActiveTeachers = async () => {
    try {
        const response = await apiFetch(ENDPOINTS.TEACHER_STAFFING_ACTIVE_TEACHERS, {
            headers: authHeaders()
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching active teachers:', error);
        return [];
    }
};

export const manageTeacherStaffingChange = async (payload) => {
    const response = await apiFetch(ENDPOINTS.TEACHER_STAFFING_CHANGES, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders()
        },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Error al gestionar el cambio de dotación');
    }
    return response.json();
};

export const revertTeacherStaffingChange = async (changeId, returnDate) => {
    const response = await apiFetch(ENDPOINTS.TEACHER_STAFFING_REVERT(changeId), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders()
        },
        body: JSON.stringify({ return_date: returnDate })
    });
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Error al revertir el cambio de dotación');
    }
    return response.json();
};

export const getTeacherStaffingHistory = async (sectionId) => {
    try {
        const url = sectionId
            ? `${ENDPOINTS.TEACHER_STAFFING_HISTORY}?course_section_id=${sectionId}`
            : ENDPOINTS.TEACHER_STAFFING_HISTORY;
        const response = await apiFetch(url, { headers: authHeaders() });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching teacher staffing history:', error);
        return [];
    }
};
